import { useEffect, useMemo, useState } from "react";
import type { CustomerDataAdapter } from "../data/customer-adapter";
import type {
  CustomerOrderConsignmentProgress,
  CustomerOrderParcelEvents,
} from "../data/customer-api-types";

// The Delivery tab of a Tharstern-era order (customer delivery updates P05): each consignment's progress is its own
// lazy read, so a slow carrier never holds up the order. Every key the order read served is read once when the order
// is shown; the carrier's answer is cached on the server (15 minutes, for ever once delivered), so asking again
// sooner would only repeat the question. This read, and the Tharstern-era page, retire together.

/** One consignment's read, as the page holds it. A key with no entry has not been asked about. */
export type ConsignmentProgressState =
  | { status: "loading" }
  | { status: "ready"; answer: CustomerOrderConsignmentProgress }
  /** The call itself failed, or the key was not found: the page shows nothing new and keeps its link. */
  | { status: "failed" };

export type ConsignmentProgressByKey = Readonly<
  Record<string, ConsignmentProgressState>
>;

/** What the parcel timeline component draws: the carrier's answer for one consignment, or nothing yet. */
export type OrderParcelEventsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; events: CustomerOrderParcelEvents }
  | { status: "failed" };

/**
 * A consignment's read as the timeline draws it: idle where nothing was asked or the carrier's answer is not to be
 * shown (a number it cannot check, a delay: the server sends no `progress`).
 */
export function parcelEventsState(
  state: ConsignmentProgressState | undefined,
): OrderParcelEventsState {
  if (!state) return { status: "idle" };
  if (state.status !== "ready") return state;
  return state.answer.progress
    ? { status: "ready", events: state.answer.progress }
    : { status: "idle" };
}

/**
 * Reads the progress of every consignment key once, for the order `orderRef` (any one of its items; the server finds
 * the order). Answers arrive as they come; a failed read leaves that consignment as the order read served it.
 */
export function useOrderConsignmentProgress(
  adapter: CustomerDataAdapter,
  orderRef: string | null | undefined,
  keys: readonly string[],
  viewKey: unknown,
): ConsignmentProgressByKey {
  const [byKey, setByKey] = useState<ConsignmentProgressByKey>({});
  // The effect is keyed on the keys' content, not on a new array each render.
  const keyList = keys.join("\u0000");
  const keySet = useMemo(
    () => (keyList ? [...new Set(keyList.split("\u0000"))] : []),
    [keyList],
  );

  useEffect(() => {
    if (!orderRef || keySet.length === 0) {
      setByKey({});
      return;
    }
    let cancelled = false;
    setByKey(
      Object.fromEntries(
        keySet.map((key) => [key, { status: "loading" as const }]),
      ),
    );
    for (const key of keySet) {
      adapter
        .getOrderConsignmentProgress(orderRef, key)
        .then((answer) => {
          if (cancelled) return;
          setByKey((current) => ({
            ...current,
            [key]: answer ? { status: "ready", answer } : { status: "failed" },
          }));
        })
        .catch(() => {
          if (cancelled) return;
          setByKey((current) => ({ ...current, [key]: { status: "failed" } }));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [adapter, orderRef, keySet, viewKey]);

  return byKey;
}
